import { Component, Input } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import ContainerStatus from '../../../../../shared/Container/model/ContainerStatus';
import { ContainerMovementListItem } from '../../../../../shared/Container/model/ContainerMovementListItem';
import { ContainerStatusEnum } from '../../../../../shared/Container/enum/ContainerStatusEnum';
import { JobTypeEnum } from '../../../../../shared/Container/enum/JobTypeEnum';
import ContainerJobUpdateStatusRequest from '../../../../../shared/Container/model/ContainerUpdateStatusRequest';
import { ContainerStatusGroup } from '../../../../../shared/Container/constant/ContainerStatusGroup';
import { LegTypeEnum } from '../../../../../shared/Container/enum/LegTypeEnum';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ContainerService } from '../../../../../shared/Container/container.service';
import { ToasterService } from '../../../../../shared/toaster/toaster.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-container-change-status-modal',
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule],
  templateUrl: './container-change-status-modal.component.html',
  styleUrl: './container-change-status-modal.component.scss'
})
export class ContainerChangeStatusModalComponent {
  @Input() containerJobItems: ContainerMovementListItem[] = [];
  changeStatusForm: FormGroup = new FormGroup(
    {
      containerGroups: new FormArray([])
    }
  );
  containerStatusList: ContainerStatus[] = [];
  containerStatusGroups: any[] = [];
  constructor(
    public activeModal: NgbActiveModal,
    private containerService: ContainerService,
    private toasterService: ToasterService){
    this.containerService.getContainerStatusList().subscribe(codes => {
      this.containerStatusList = codes;
    });
  }

  ngOnInit() {
    if (this.containerJobItems && this.containerJobItems.length > 0) {
      const statusGroups = this.containerJobItems.reduce((acc, record) => {
        if (record) {
          const status = record.containerStatus!.transitStatus ?
            record.containerStatus!.transitStatus!.description : 'invalid';

          if (status !== 'invalid'){
            const containerNumber = record.containerNumber;
            const isHeld = false;
            const jobType = record.jobType;
            const legType = record.legType;
            const containerJobItemId = record.containerJobItemId;

            const key = `${status}-${isHeld}-${jobType}`;

            if (!acc[key]) {
              acc[key] = [];
            }

            acc[key].push({ containerJobItemId, containerNumber, jobType, legType, isHeld });
          }
        }
        return acc;
      }, {} as Record<string, { containerJobItemId: number, containerNumber: string, jobType: string, legType: string, isHeld: boolean }[]>);

      this.containerStatusGroups = Object.keys(statusGroups).map(key => {
        const [status, isHeld, jobType] = key.split('-');
        const legTypes = statusGroups[key].map(c => c.legType);
        const distinctLegTypes = [...new Set(legTypes)];

        return {
          containerNumbers: statusGroups[key].map(c => c.containerNumber.trim()),
          currentStatus: status,
          isHeld: isHeld === 'true',
          jobType: jobType,
          legTypes: distinctLegTypes,
          applicableStatusList: this.getContainerStatusGroup(status, jobType, legTypes),
          containerJobItemIds: statusGroups[key].map(c => c.containerJobItemId)
        };
      });
      this.createContainerGroupItems();
    }
  }

  get containerGroups(): FormArray {
    return this.changeStatusForm.get('containerGroups') as FormArray;
  }

  getContainerStatusGroup(currentStatus: string, jobType: string, legTypes: string[]) {
    const jobTypeEnum: JobTypeEnum = JobTypeEnum[jobType as keyof typeof JobTypeEnum];

    if (jobTypeEnum === JobTypeEnum.Import || jobTypeEnum === JobTypeEnum.DomesticInbound || jobTypeEnum === JobTypeEnum.RailInbound) {
      return this.getImportContainerStatusGroup(currentStatus);
    } else if (jobTypeEnum === JobTypeEnum.Export || jobTypeEnum === JobTypeEnum.DomesticOutbound || jobTypeEnum === JobTypeEnum.RailOutbound) {
      return this.getExportContainerStatusGroup(currentStatus);
    } else if (jobTypeEnum === JobTypeEnum.IntermodalCoastal || jobTypeEnum === JobTypeEnum.IntermodalRail) {
      const isOutbound = legTypes.includes(LegTypeEnum.Outbound) || legTypes.includes(LegTypeEnum.OutboundEmpty);
      const isInbound = legTypes.includes(LegTypeEnum.Inbound) || legTypes.includes(LegTypeEnum.InboundEmpty);

      if (isOutbound && !isInbound) {
        return this.getExportContainerStatusGroup(currentStatus);
      } else if (isInbound && !isOutbound) {
        return this.getImportContainerStatusGroup(currentStatus);
      }
    }
    return undefined;
  }

  getExportContainerStatusGroup(currentStatus: string): ContainerStatusGroup {
    if (currentStatus === ContainerStatusEnum.Active) {
      return ContainerStatusGroup.ExportActive;
    } else if (currentStatus === ContainerStatusEnum.ShipAtWharf) {
      return ContainerStatusGroup.ExportSaw;
    } else if (currentStatus === ContainerStatusEnum.Wharf) {
      return ContainerStatusGroup.ExportWharf;
    } else if (currentStatus === ContainerStatusEnum.OnVessel) {
      return ContainerStatusGroup.ExportVessel;
    } else if (currentStatus === ContainerStatusEnum.Departed) {
      return ContainerStatusGroup.ExportDeparted;
    } else if (currentStatus === ContainerStatusEnum.Missed) {
      return ContainerStatusGroup.ExportMissed;
    } else if (currentStatus === ContainerStatusEnum.Hold) {
      return ContainerStatusGroup.ExportHold;
    } else if (currentStatus === ContainerStatusEnum.Pack) {
      return ContainerStatusGroup.ReadyForPickup;
    } else if (currentStatus === ContainerStatusEnum.ReadyForPickup) {
      return ContainerStatusGroup.Packed;
    } else if (currentStatus === ContainerStatusEnum.Unknown) {
      return ContainerStatusGroup.ExportUnknown;
    } else {
      return [];
    }
  }

  getImportContainerStatusGroup(currentStatus: string): ContainerStatusGroup {
    if (currentStatus === ContainerStatusEnum.Active) {
      return ContainerStatusGroup.ImportActive;
    } else if (currentStatus === ContainerStatusEnum.Inbound) {
      return ContainerStatusGroup.ImportInbound;
    } else if (currentStatus === ContainerStatusEnum.Arrived) {
      return ContainerStatusGroup.ImportArrived;
    } else if (currentStatus === ContainerStatusEnum.Wharf) {
      return ContainerStatusGroup.ImportWharf;
    } else if (currentStatus === ContainerStatusEnum.Hold) {
      return ContainerStatusGroup.ImportHold;
    } else if (currentStatus === ContainerStatusEnum.Available) {
      return ContainerStatusGroup.ImportAvailable;
    } else if (currentStatus === ContainerStatusEnum.GateOut) {
      return ContainerStatusGroup.ImportGateOut;
    } else if (currentStatus === ContainerStatusEnum.Unpack) {
      return ContainerStatusGroup.ReadyForPickup;
    } else if (currentStatus === ContainerStatusEnum.ReadyForPickup) {
      return ContainerStatusGroup.Unpack;
    } else if (currentStatus === ContainerStatusEnum.OnRail) {
      return ContainerStatusGroup.ImportOnRail;
    } else if (currentStatus === ContainerStatusEnum.Unknown) {
      return ContainerStatusGroup.ImportUnknown;
    } else {
      return [];
    }
  }

  createContainerGroupItem(group: any): FormGroup {
    let formGroup = new FormGroup({
      containerJobItemIds: new FormControl(group.containerJobItemIds),
      applicableStatus: new FormControl(null),
      isHeld: new FormControl(group.isHeld),
      legTypes: new FormControl(group.legTypes)
    });

    if (group.currentStatus === ContainerStatusEnum.ReadyForPickup
      || group.currentStatus === ContainerStatusEnum.Unpack
      || group.currentStatus === ContainerStatusEnum.Pack) {
      formGroup.get('isHeld')!.disable();
    }

    return formGroup;
  }

  createContainerGroupItems() {
    if (this.containerStatusGroups) {
      this.containerStatusGroups.forEach((group) => {
        this.containerGroups.push(this.createContainerGroupItem(group));
      });
    }
  }

  onCheckboxChange(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.containerStatusGroups[index].isHeld = input.checked;
  }

  async onSubmit() {
    const formValues = this.changeStatusForm.getRawValue();
    const groups = formValues.containerGroups;

    if (groups) {
      let model: ContainerJobUpdateStatusRequest[] = [];
      groups.forEach((group: any) => {
        if (group.applicableStatus) {
          const newStatus = this.containerStatusList.find(x => x.description == group.applicableStatus.name);

          model.push({
            containerJobItemIds: group.containerJobItemIds,
            containerStatus: { ...newStatus! },
            isHeld: group.isHeld,
            legTypes: group.legTypes
          })
        }
      });

      if (model && model.length > 0) {
        this.containerService.updateContainerJobItemStatus(model).subscribe({
          next: (response) => {
            this.activeModal.close(true);
            this.toasterService.showDefault("The selected container(s) has been successfully updated!")
          },
          error: (errorResponse: HttpErrorResponse) => {
            this.toasterService.showDanger("Failed to change container status!")
          }
        });
      }
    }
  }
}
